import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type { AvgPrepTimeResult } from "../../domain/repo/get-avg-prep-time";
import type { OrderThroughputItem } from "../../domain/repo/get-order-throughput";
import type { SalesByBranchItem } from "../../domain/repo/get-sales-by-branch";
import type { SalesByDayItem } from "../../domain/repo/get-sales-by-day";
import type { SalesSummary } from "../../domain/repo/get-sales-summary";
import type { WalletApprovalsSummary } from "../../domain/repo/get-wallet-approvals-summary";
import type { WalletSummary } from "../../domain/repo/get-wallet-summary";
import type { WalletTransactionRow } from "../../domain/repo/get-wallet-transactions";
import type { InventorySummary } from "../../domain/repo/get-inventory-summary";
import type { ExpiryLotRow } from "../../domain/repo/get-expiry-lots";
import type { WastageReportItem } from "../../domain/repo/get-wastage-report";
import type { TransferReportAgingRow } from "../../domain/repo/transfer/get-transfer-report-aging";
import type { TransferReportByBranchRow } from "../../domain/repo/transfer/get-transfer-report-by-branch";
import type { TransferReportByCategoryRow } from "../../domain/repo/transfer/get-transfer-report-by-category";
import type { TransferReportByItemRow } from "../../domain/repo/transfer/get-transfer-report-by-item";
import type { TransferReportLineRow } from "../../domain/repo/transfer/get-transfer-report-lines";
import type { TransferReportSummary } from "../../domain/repo/transfer/get-transfer-report-summary";
import type { TransferReportTrendRow } from "../../domain/repo/transfer/get-transfer-report-trend";
import type { LowStockItemRow } from "@/modules/inventory/domain/types";
import type { PurchaseOrderReportBySupplierRow } from "../../domain/repo/purchase-order/get-purchase-order-report-by-supplier";
import type { PurchaseOrderLeadTimeReport } from "../../domain/repo/purchase-order/get-purchase-order-lead-time-report";
import type { PurchaseOrderReportSummary } from "../../domain/repo/purchase-order/get-purchase-order-report-summary";
import type {
  TransferReportDateField,
  TransferTrendGroupBy,
} from "../../domain/repo/transfer/transfer-report-filters";
import type { StockTransferStatus } from "@/server/platform/db/schema/stock-transfers";

export type SalesSummaryParams = {
  fromDate?: Date;
  toDate?: Date;
  branchIds?: string[];
  channel?: "APP" | "WALKIN";
  statusFilter?: "all" | "completed" | "pending";
};

export type SalesByDayParams = SalesSummaryParams & {
  groupBy?: "day" | "month";
};

export type OperationsParams = {
  fromDate?: Date;
  toDate?: Date;
  branchIds?: string[];
  channel?: "APP" | "WALKIN";
};

export type OrderThroughputParams = OperationsParams & {
  groupBy?: "hour" | "day";
};

export type WalletParams = {
  fromDate?: Date;
  toDate?: Date;
  transactionTypes?: ("TOPUP" | "PAYMENT" | "REFUND" | "ADJUSTMENT")[];
  customerId?: string;
  status?: "PENDING" | "APPROVED" | "POSTED" | "REJECTED";
};

export type WalletTransactionsParams = WalletParams & {
  limit?: number;
  offset?: number;
};

function buildSearchParams(params?: SalesSummaryParams): URLSearchParams {
  const searchParams = new URLSearchParams();

  if (params?.fromDate) {
    searchParams.set("fromDate", params.fromDate.toISOString());
  }
  if (params?.toDate) {
    searchParams.set("toDate", params.toDate.toISOString());
  }
  if (params?.branchIds && params.branchIds.length > 0) {
    searchParams.set("branchIds", params.branchIds.join(","));
  }
  if (params?.channel) {
    searchParams.set("channel", params.channel);
  }
  if (params?.statusFilter) {
    searchParams.set("statusFilter", params.statusFilter);
  }

  return searchParams;
}

export const reportsApi = {
  getSalesSummary: async (
    params?: SalesSummaryParams,
  ): Promise<SalesSummary> => {
    const searchParams = buildSearchParams(params);
    const url = `${config.apiUrl}/reports/sales/summary${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<SalesSummary>(url);
  },

  getSalesByDay: async (
    params?: SalesByDayParams,
  ): Promise<SalesByDayItem[]> => {
    const searchParams = buildSearchParams(params);
    if (params?.groupBy) {
      searchParams.set("groupBy", params.groupBy);
    }
    const url = `${config.apiUrl}/reports/sales/by-day${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<SalesByDayItem[]>(url);
  },

  getSalesByBranch: async (
    params?: SalesSummaryParams,
  ): Promise<SalesByBranchItem[]> => {
    const searchParams = buildSearchParams(params);
    const url = `${config.apiUrl}/reports/sales/by-branch${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<SalesByBranchItem[]>(url);
  },

  getSalesExport: async (
    params?: SalesByDayParams,
  ): Promise<{
    salesByDay: SalesByDayItem[];
    salesByBranch: SalesByBranchItem[];
  }> => {
    const searchParams = buildSearchParams(params);
    if (params?.groupBy) {
      searchParams.set("groupBy", params.groupBy);
    }
    const url = `${config.apiUrl}/reports/sales/export${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<{
      salesByDay: SalesByDayItem[];
      salesByBranch: SalesByBranchItem[];
    }>(url);
  },

  // Operations Reports
  getAvgPrepTime: async (
    params?: OperationsParams,
  ): Promise<AvgPrepTimeResult[]> => {
    const searchParams = buildOperationsParams(params);
    const url = `${config.apiUrl}/reports/ops/avg-prep-time${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<AvgPrepTimeResult[]>(url);
  },

  getOrderThroughput: async (
    params?: OrderThroughputParams,
  ): Promise<OrderThroughputItem[]> => {
    const searchParams = buildOperationsParams(params);
    if (params?.groupBy) {
      searchParams.set("groupBy", params.groupBy);
    }
    const url = `${config.apiUrl}/reports/ops/throughput${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<OrderThroughputItem[]>(url);
  },

  getOperationsExport: async (
    params?: OrderThroughputParams,
  ): Promise<{
    avgPrepTime: AvgPrepTimeResult[];
    throughput: OrderThroughputItem[];
  }> => {
    const searchParams = buildOperationsParams(params);
    if (params?.groupBy) {
      searchParams.set("groupBy", params.groupBy);
    }
    const url = `${config.apiUrl}/reports/ops/export${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<{
      avgPrepTime: AvgPrepTimeResult[];
      throughput: OrderThroughputItem[];
    }>(url);
  },

  // Wallet Reports
  getWalletSummary: async (params?: WalletParams): Promise<WalletSummary> => {
    const searchParams = buildWalletParams(params);
    const url = `${config.apiUrl}/reports/wallet/summary${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<WalletSummary>(url);
  },

  getWalletTransactions: async (
    params?: WalletTransactionsParams,
  ): Promise<{ items: WalletTransactionRow[]; total: number }> => {
    const searchParams = buildWalletParams(params);
    if (params?.limit) {
      searchParams.set("limit", String(params.limit));
    }
    if (params?.offset) {
      searchParams.set("offset", String(params.offset));
    }
    const url = `${config.apiUrl}/reports/wallet/transactions${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<{ items: WalletTransactionRow[]; total: number }>(url);
  },

  getWalletApprovalsSummary: async (params?: {
    fromDate?: Date;
    toDate?: Date;
    status?: "PENDING" | "APPROVED" | "REJECTED";
  }): Promise<WalletApprovalsSummary> => {
    const searchParams = new URLSearchParams();
    if (params?.fromDate) {
      searchParams.set("fromDate", params.fromDate.toISOString());
    }
    if (params?.toDate) {
      searchParams.set("toDate", params.toDate.toISOString());
    }
    if (params?.status) {
      searchParams.set("status", params.status);
    }
    const url = `${config.apiUrl}/reports/wallet/approvals-summary${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<WalletApprovalsSummary>(url);
  },

  getWalletExport: async (
    params?: WalletTransactionsParams,
  ): Promise<{ transactions: WalletTransactionRow[]; total: number }> => {
    const searchParams = buildWalletParams(params);
    // For export, use high limit
    searchParams.set("limit", String(params?.limit ?? 10000));
    searchParams.set("offset", String(params?.offset ?? 0));
    const url = `${config.apiUrl}/reports/wallet/export${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<{ transactions: WalletTransactionRow[]; total: number }>(
      url,
    );
  },

  // Inventory Reports
  getInventorySummary: async (params?: InventoryParams): Promise<InventorySummary> => {
    const searchParams = buildInventoryParams(params);
    const url = `${config.apiUrl}/reports/inventory/summary${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<InventorySummary>(url);
  },

  getExpiryLots: async (
    params?: ExpiryLotsParams,
  ): Promise<{ items: ExpiryLotRow[]; total: number }> => {
    const searchParams = buildInventoryParams(params);
    if (params?.limit) {
      searchParams.set("limit", String(params.limit));
    }
    if (params?.offset) {
      searchParams.set("offset", String(params.offset));
    }
    const url = `${config.apiUrl}/reports/inventory/expiry-lots${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<{ items: ExpiryLotRow[]; total: number }>(url);
  },

  getWastageReport: async (
    params?: InventoryParams,
  ): Promise<WastageReportItem[]> => {
    const searchParams = buildInventoryParams(params);
    const url = `${config.apiUrl}/reports/inventory/wastage${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<WastageReportItem[]>(url);
  },

  getInventoryExport: async (
    params?: ExpiryLotsParams & InventoryParams,
  ): Promise<{
    expiryLots: ExpiryLotRow[];
    expiryLotsTotal: number;
    wastage: WastageReportItem[];
  }> => {
    const searchParams = buildInventoryParams(params);
    if (params?.limit) {
      searchParams.set("limit", String(params.limit));
    }
    if (params?.offset) {
      searchParams.set("offset", String(params.offset));
    }
    const url = `${config.apiUrl}/reports/inventory/export${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<{
      expiryLots: ExpiryLotRow[];
      expiryLotsTotal: number;
      wastage: WastageReportItem[];
    }>(url);
  },

  getTransferReportSummary: async (
    params?: TransferReportParams,
  ): Promise<TransferReportSummary> => {
    const searchParams = buildTransferReportParams(params);
    const url = `${config.apiUrl}/reports/inventory/transfers/summary${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<TransferReportSummary>(url);
  },

  getTransferReportByBranch: async (
    params?: TransferReportParams,
  ): Promise<TransferReportByBranchRow[]> => {
    const searchParams = buildTransferReportParams(params);
    const url = `${config.apiUrl}/reports/inventory/transfers/by-branch${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<TransferReportByBranchRow[]>(url);
  },

  getTransferReportByItem: async (
    params?: TransferReportParams,
  ): Promise<TransferReportByItemRow[]> => {
    const searchParams = buildTransferReportParams(params);
    const url = `${config.apiUrl}/reports/inventory/transfers/by-item${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<TransferReportByItemRow[]>(url);
  },

  getTransferReportByCategory: async (
    params?: TransferReportParams,
  ): Promise<TransferReportByCategoryRow[]> => {
    const searchParams = buildTransferReportParams(params);
    const url = `${config.apiUrl}/reports/inventory/transfers/by-category${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<TransferReportByCategoryRow[]>(url);
  },

  getTransferReportTrend: async (
    params?: TransferReportParams,
  ): Promise<TransferReportTrendRow[]> => {
    const searchParams = buildTransferReportParams(params);
    const url = `${config.apiUrl}/reports/inventory/transfers/trend${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<TransferReportTrendRow[]>(url);
  },

  getTransferReportAging: async (
    params?: TransferReportAgingParams,
  ): Promise<TransferReportAgingRow[]> => {
    const searchParams = new URLSearchParams();
    if (params?.minAgeDays != null) {
      searchParams.set("minAgeDays", String(params.minAgeDays));
    }
    if (params?.limit != null) {
      searchParams.set("limit", String(params.limit));
    }
    if (params?.statuses?.length) {
      searchParams.set("statuses", params.statuses.join(","));
    }
    const url = `${config.apiUrl}/reports/inventory/transfers/aging${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<TransferReportAgingRow[]>(url);
  },

  getTransferReportLines: async (
    params?: TransferReportLinesParams,
  ): Promise<{ items: TransferReportLineRow[]; total: number }> => {
    const searchParams = buildTransferReportParams(params);
    if (params?.limit != null) {
      searchParams.set("limit", String(params.limit));
    }
    if (params?.offset != null) {
      searchParams.set("offset", String(params.offset));
    }
    const url = `${config.apiUrl}/reports/inventory/transfers/lines${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<{ items: TransferReportLineRow[]; total: number }>(url);
  },

  getTransferReportExport: async (
    params?: TransferReportParams,
  ): Promise<{
    summary: TransferReportSummary;
    byBranch: TransferReportByBranchRow[];
    byItem: TransferReportByItemRow[];
    byCategory: TransferReportByCategoryRow[];
    lines: TransferReportLineRow[];
    linesTotal: number;
  }> => {
    const searchParams = buildTransferReportParams(params);
    const url = `${config.apiUrl}/reports/inventory/transfers/export${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get(url);
  },

  getPurchaseOrderReportSummary: async (
    params?: PurchaseOrderReportParams,
  ): Promise<PurchaseOrderReportSummary> => {
    const searchParams = buildPurchaseOrderReportParams(params);
    const url = `${config.apiUrl}/reports/inventory/purchase-orders/summary${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<PurchaseOrderReportSummary>(url);
  },

  getPurchaseOrderLeadTimeReport: async (
    params?: PurchaseOrderReportParams,
  ): Promise<PurchaseOrderLeadTimeReport> => {
    const searchParams = buildPurchaseOrderReportParams(params);
    const url = `${config.apiUrl}/reports/inventory/purchase-orders/lead-time${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get<PurchaseOrderLeadTimeReport>(url);
  },

  getPurchaseOrderReportBySupplier: async (
    params?: PurchaseOrderReportParams,
  ): Promise<PurchaseOrderReportBySupplierRow[]> => {
    const searchParams = buildPurchaseOrderReportParams(params);
    const url = `${config.apiUrl}/reports/inventory/purchase-orders/by-supplier${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    const res = await fetcher.get<{ items: PurchaseOrderReportBySupplierRow[] }>(
      url,
    );
    return res.items;
  },

  getPurchaseOrderReportExport: async (
    params?: PurchaseOrderReportParams,
  ): Promise<{
    summary: PurchaseOrderReportSummary;
    bySupplier: PurchaseOrderReportBySupplierRow[];
    leadTime: PurchaseOrderLeadTimeReport;
  }> => {
    const searchParams = buildPurchaseOrderReportParams(params);
    const url = `${config.apiUrl}/reports/inventory/purchase-orders/export${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return fetcher.get(url);
  },

  getLowStockReport: async (
    params: LowStockReportParams,
  ): Promise<{ items: LowStockItemRow[]; total: number }> => {
    const searchParams = buildLowStockReportParams(params);
    const url = `${config.apiUrl}/reports/inventory/low-stock?${searchParams.toString()}`;
    return fetcher.get(url);
  },

  getLowStockReportExport: async (
    params: Omit<LowStockReportParams, "limit" | "offset">,
  ): Promise<{ items: LowStockItemRow[]; total: number }> => {
    const searchParams = buildLowStockReportParams(params);
    const url = `${config.apiUrl}/reports/inventory/low-stock/export?${searchParams.toString()}`;
    return fetcher.get(url);
  },
};

export type TransferReportParams = {
  fromDate?: Date;
  toDate?: Date;
  dateField?: TransferReportDateField;
  fromBranchIds?: string[];
  statuses?: StockTransferStatus[];
  groupBy?: TransferTrendGroupBy;
  ingredientCategoryId?: string;
};

export type TransferReportAgingParams = {
  minAgeDays?: number;
  limit?: number;
  statuses?: StockTransferStatus[];
};

export type TransferReportLinesParams = TransferReportParams & {
  limit?: number;
  offset?: number;
};

function buildTransferReportParams(
  params?: TransferReportParams,
): URLSearchParams {
  const searchParams = new URLSearchParams();
  if (params?.fromDate) {
    searchParams.set("fromDate", params.fromDate.toISOString());
  }
  if (params?.toDate) {
    searchParams.set("toDate", params.toDate.toISOString());
  }
  if (params?.dateField) {
    searchParams.set("dateField", params.dateField);
  }
  if (params?.fromBranchIds?.length) {
    searchParams.set("fromBranchIds", params.fromBranchIds.join(","));
  }
  if (params?.statuses?.length) {
    searchParams.set("statuses", params.statuses.join(","));
  }
  if (params?.groupBy) {
    searchParams.set("groupBy", params.groupBy);
  }
  if (params?.ingredientCategoryId) {
    searchParams.set("ingredientCategoryId", params.ingredientCategoryId);
  }
  return searchParams;
}

export type InventoryParams = {
  branchIds?: string[];
  itemType?: "INGREDIENT" | "RETAIL_VARIANT";
  itemId?: string;
  daysBeforeExpiry?: number;
  groupBy?: "day" | "week" | "month";
};

export type ExpiryLotsParams = InventoryParams & {
  limit?: number;
  offset?: number;
};

function buildInventoryParams(params?: InventoryParams): URLSearchParams {
  const searchParams = new URLSearchParams();

  if (params?.branchIds && params.branchIds.length > 0) {
    searchParams.set("branchIds", params.branchIds.join(","));
  }
  if (params?.itemType) {
    searchParams.set("itemType", params.itemType);
  }
  if (params?.itemId) {
    searchParams.set("itemId", params.itemId);
  }
  if (params?.daysBeforeExpiry !== undefined) {
    searchParams.set("daysBeforeExpiry", String(params.daysBeforeExpiry));
  }
  if (params?.groupBy) {
    searchParams.set("groupBy", params.groupBy);
  }

  return searchParams;
}

function buildWalletParams(params?: WalletParams): URLSearchParams {
  const searchParams = new URLSearchParams();

  if (params?.fromDate) {
    searchParams.set("fromDate", params.fromDate.toISOString());
  }
  if (params?.toDate) {
    searchParams.set("toDate", params.toDate.toISOString());
  }
  if (params?.transactionTypes && params.transactionTypes.length > 0) {
    searchParams.set("transactionTypes", params.transactionTypes.join(","));
  }
  if (params?.customerId) {
    searchParams.set("customerId", params.customerId);
  }
  if (params?.status) {
    searchParams.set("status", params.status);
  }

  return searchParams;
}

function buildOperationsParams(params?: OperationsParams): URLSearchParams {
  const searchParams = new URLSearchParams();

  if (params?.fromDate) {
    searchParams.set("fromDate", params.fromDate.toISOString());
  }
  if (params?.toDate) {
    searchParams.set("toDate", params.toDate.toISOString());
  }
  if (params?.branchIds && params.branchIds.length > 0) {
    searchParams.set("branchIds", params.branchIds.join(","));
  }
  if (params?.channel) {
    searchParams.set("channel", params.channel);
  }

  return searchParams;
}

export type PurchaseOrderReportParams = {
  orderDateFrom?: string;
  orderDateTo?: string;
  supplierId?: string;
  branchId?: string;
};

function buildPurchaseOrderReportParams(
  params?: PurchaseOrderReportParams,
): URLSearchParams {
  const searchParams = new URLSearchParams();
  if (params?.orderDateFrom) {
    searchParams.set("orderDateFrom", params.orderDateFrom);
  }
  if (params?.orderDateTo) {
    searchParams.set("orderDateTo", params.orderDateTo);
  }
  if (params?.supplierId) {
    searchParams.set("supplierId", params.supplierId);
  }
  if (params?.branchId) {
    searchParams.set("branchId", params.branchId);
  }
  return searchParams;
}

export type LowStockReportParams = {
  branchId: string;
  itemType?: "INGREDIENT" | "RETAIL_VARIANT";
  categoryId?: string;
  limit?: number;
  offset?: number;
};

function buildLowStockReportParams(
  params: Omit<LowStockReportParams, "limit" | "offset"> &
    Pick<Partial<LowStockReportParams>, "limit" | "offset">,
): URLSearchParams {
  const searchParams = new URLSearchParams();
  searchParams.set("branchId", params.branchId);
  if (params.itemType) searchParams.set("itemType", params.itemType);
  if (params.categoryId) searchParams.set("categoryId", params.categoryId);
  if (params.limit != null) searchParams.set("limit", String(params.limit));
  if (params.offset != null) searchParams.set("offset", String(params.offset));
  return searchParams;
}
